import { IonCol, IonContent, IonPage, IonRow, useIonRouter } from '@ionic/react'
import { useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { zodResolver } from '@hookform/resolvers/zod'
import * as R from 'remeda'
import { z } from 'zod'

import { useToast } from '../../contexts/toastContext'
import { StyledError } from '../../components/ui/form/errors'
import { PhoneNumberInput, TextInput } from '../../components/ui/form/input'
import { useUpdateClient } from '../../hooks/clients/mutations'
import { useClientInfo } from '../../clients/client-info-store'
import { updateClientInputSchema } from '../../marketing/customer-opt-in/customer-opt-in.schema'
import { useCustomerOptInClientId } from '../../marketing/customer-opt-in/utils'

import type { UpdateClientInput } from '../../marketing/customer-opt-in/customer-opt-in.schema'

function RotateMoreInfoPage() {
  return (
    <IonPage>
      <IonContent>
        <MoreInfo />
      </IonContent>
    </IonPage>
  )
}

function MoreInfo() {
  const router = useIonRouter()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const clientId = useCustomerOptInClientId()
  const setToast = useToast()

  const { dobObject, ...clientInfo } = useClientInfo()
  const updateClient = useUpdateClient()

  const { email, firstName, lastName, phoneNumber } = R.mapValues(
    clientInfo,
    (v) => v ?? ''
  )

  // TODO: This is a quick fix for twb June 29
  const schema = updateClientInputSchema
    .omit({ firstName: true })
    .extend({ firstName: z.string().min(1, 'First Name is required.') })

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateClientInput>({
    defaultValues: {
      email,
      firstName,
      lastName,
      addE164: phoneNumber,
      birthday: {
        day: dobObject?.day ?? null,
        month: dobObject?.month ?? null,
      },
    },
    resolver: zodResolver(schema),
  })

  function handleFinish(data: UpdateClientInput) {
    if (!clientId)
      return setToast({
        message: "There isn't an available id to update.",
        color: 'danger',
      })

    updateClient.mutate(
      { objectId: clientId, ...data },
      {
        onSuccess: () => {
          router.push(
            `/rotate/opt-in/success?${searchParams}`,
            'none',
            'replace'
          )
        },
      }
    )
  }

  return (
    <form onSubmit={handleSubmit(handleFinish)} className="h-full">
      <div className="mx-auto flex size-full max-w-xl flex-col items-center justify-center">
        <h1 className="text-5xl">Welcome!</h1>
        <div className="py-3" />
        <p className="text-3xl">We just need a little more info from you.</p>
        <div className="py-6" />

        <IonRow className="w-full">
          <IonCol size="6">
            <TextInput
              disabled={updateClient.isLoading}
              control={control}
              name="firstName"
              label="First Name (required)"
            />
            <ErrorMessage name="firstName" as={StyledError} errors={errors} />
          </IonCol>
          <IonCol size="6">
            <TextInput
              disabled={updateClient.isLoading}
              control={control}
              name="lastName"
              label="Last Name"
            />
          </IonCol>
        </IonRow>

        <IonRow className="w-full">
          <IonCol size="7">
            <TextInput
              disabled={updateClient.isLoading}
              control={control}
              name="email"
              label="Email"
            />
          </IonCol>

          <IonCol size="5">
            <PhoneNumberInput
              label="Phone Number"
              control={control}
              name="addE164"
              placeholder="Phone Number"
              defaultCountry="US"
            />
          </IonCol>
        </IonRow>

        <IonRow className="w-full items-start">
          <IonCol size="5">
            <div>
              <p className="font-semibold">Birthday</p>
              <div className="flex gap-x-3">
                <TextInput
                  control={control}
                  name="birthday.month"
                  label="Month"
                />
                <TextInput control={control} name="birthday.day" label="Day" />
              </div>
              <ErrorMessage
                as={StyledError}
                errors={errors}
                name="birthday.month"
              />
              <ErrorMessage
                as={StyledError}
                errors={errors}
                name="birthday.day"
              />
            </div>
          </IonCol>
        </IonRow>

        <div className="py-10" />
        <button
          disabled={updateClient.isLoading}
          type="submit"
          color="secondary"
          className="bg-ion-color-secondary text-ion-color-primary rounded-full px-20 py-3 text-3xl font-semibold"
        >
          Continue
        </button>
      </div>
    </form>
  )
}

export { RotateMoreInfoPage }
